"use client";

import { NewsCard } from "@/components/NewsCard";
import { Load } from "@/components/icons/Load";
import { Spinner } from "@/components/icons/Spinner";
import {
  TypeArticleCategory,
  TypeArticleCategorySkeleton,
  TypeArticleSkeleton,
} from "@/lib/contentful/contentfulTypes";
import { MButton } from "@/ui/MButton";
import { Button, NativeSelect } from "@mantine/core";
import clsx from "clsx";
import { Entry, EntryCollection } from "contentful";
import { useState } from "react";

export function NewsList({
  locale,
  initialNews,
  newsCategories,
}: {
  locale: string;
  initialNews: Entry<
    TypeArticleSkeleton,
    "WITHOUT_UNRESOLVABLE_LINKS",
    string
  >[];
  newsCategories: Entry<
    TypeArticleCategorySkeleton,
    "WITHOUT_UNRESOLVABLE_LINKS",
    string
  >[];
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(initialNews);
  const categories = ["All", ...newsCategories.map((n) => n.fields.title)];
  const [selectedCategory, setSelectedCategory] = useState("All");

  const setCategoryFilter = (searchValue: string) => {
    const value = categories.includes(searchValue) ? searchValue : "All";
    setSelectedCategory(value === selectedCategory ? "All" : value);
  };

  const toogleSelectionColor = (searchValue: string): boolean => {
    return categories.includes(searchValue) && searchValue === selectedCategory;
  };

  const handleOnClick = () => {
    setLoading(true);
    fetch(`/api/${locale}/news?skip=${data.length}`)
      .then((d) => d.json())
      .then(
        (
          newData: EntryCollection<
            TypeArticleSkeleton,
            "WITHOUT_UNRESOLVABLE_LINKS",
            string
          >,
        ) => {
          const sortedData = [...data, ...newData.items].sort((a, b) =>
            b.sys.updatedAt.localeCompare(a.sys.updatedAt),
          );
          setData(sortedData);
        },
      )
      .finally(() => setLoading(false));
  };

  return (
    <section>
      <section
        className={clsx("flex", "items-center", "justify-between", "md:hidden")}
      >
        <p className={clsx("p-3", "text-xl")}>Filter</p>
        <NativeSelect
          data={categories}
          radius={"md"}
          onChange={(event) => {
            setCategoryFilter(event.currentTarget.value);
          }}
          className={clsx(
            "p-3",
            "rounded-3xl",
            "border-2",
            "m-1",
            "py-2",
            "px-4",
            "border-black",
          )}
        />
      </section>
      <section className={clsx("flex", "flex-wrap", "p-10")}>
        {categories.map((dep) => (
          <Button
            onClick={() => setCategoryFilter(dep)}
            key={dep}
            className={clsx(
              "cursor-pointer",
              "md:block",
              "rounded-3xl",
              "border-2",
              "m-1",
              "py-2",
              "px-4",
              "border-black",
              "min-w-24",
              "align-middle",
              "focus:outline-primaryOrange",
              {
                "border-primaryOrange bg-primaryOrange text-white":
                  toogleSelectionColor(dep),
                "border-gray-400 bg-transparent": !toogleSelectionColor(dep),
              },
            )}
          >
            {dep}
          </Button>
        ))}
      </section>
      <div className={clsx("grid grid-cols-1 gap-5 md:grid-cols-3")}>
        {data
          .filter(
            (news) =>
              selectedCategory === "All" ||
              news.fields.category?.fields.title === selectedCategory,
          )
          .sort((a, b) => b.sys.updatedAt.localeCompare(a.sys.updatedAt))
          .map((news) => (
            <NewsCard
              model={news}
              key={news.sys.id}
              className={clsx(
                "grid grid-cols-2 gap-2 md:block",
                "animate-fade",
              )}
            />
          ))}
      </div>
      <MButton
        className="buttonsSmall mt-5 hover:underline"
        onClick={handleOnClick}
        classNames={(_theme, props) => ({
          label: clsx("flex items-center gap-2"),
        })}
      >
        Load more
        {loading ? <Spinner className="text-primaryOrange" /> : <Load />}
      </MButton>
    </section>
  );
}
